import {
  Component,
  Inject,
  InjectionToken,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { StepperTabBarItemInterface } from '~/component/stepper/tab-bar/interfaces/item.interface';
import { TipsVariantEnum } from '~/component/tips/enum/variant.enum';
import { ParamethorService } from '~/paramethor/paramethor.service';
import { ParamethorNameEnum } from '~/paramethor/names.enum';

import { OrganizerSharedPageContainerTabBarConfigInterface } from './interfaces/tab-bar-config.interface';
import { OrganizerSharedPageContainerTipConfigInterface } from './interfaces/tip.config.interface';
import { OrganizerSharedPageContainerTipInterface } from './interfaces/tip.interface';

import { VtmnButtonVariantEnum } from '~/component/buttons/vtmn-button/enum/vtmn-button-variant.enum';
import { VtmnButtonSizeEnum } from '~/component/buttons/vtmn-button/enum/vtmn-button-size.enum';
import { OrganizerSharedOrganizationService } from '~/organizer/shared/services/organization/organization.service';
import { UserService } from '~/auth/services/user/user.service';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';

export const STEPPER_TAB_BAR_CONFIG = new InjectionToken<
  OrganizerSharedPageContainerTabBarConfigInterface[]
>('Stepper TabBar Config');

@Component({
  selector: 'app-organizer-shared-page-container',
  templateUrl: './page-container.component.html',
  styleUrls: ['./page-container.component.scss'],
})
export class OrganizerSharedPageContainerComponent
  implements OnInit, OnChanges
{
  @Input() public activeStep = -1;
  @Input() public hasSingleColumn = false;
  @Input()
  public tipsConfig: OrganizerSharedPageContainerTipConfigInterface[] = [];
  @Input() public watchTipsChanges = false;

  private readonly subscriptions = new Subscription();
  public stepperTabBarCollection: StepperTabBarItemInterface[] = [];
  public tips: OrganizerSharedPageContainerTipInterface[] = [];
  public readonly vtmnButtonVariant = VtmnButtonVariantEnum;
  public readonly vtmnButtonSize = VtmnButtonSizeEnum;
  public stripeRedirectUrl: string;

  constructor(
    @Inject(STEPPER_TAB_BAR_CONFIG)
    private readonly stepperTabBarConfig: OrganizerSharedPageContainerTabBarConfigInterface[],
    private readonly translateService: TranslateService,
    private readonly paramethorService: ParamethorService,
    private readonly organizationService: OrganizerSharedOrganizationService,
    private readonly userService: UserService
  ) {}

  public ngOnChanges(simpleChanges: SimpleChanges): void {
    if (this.watchTipsChanges && simpleChanges?.tipsConfig?.currentValue) {
      this.buildTips();
    }
  }

  public ngOnInit(): void {
    this.buildStepperTabBarCollection();
    this.buildTips();

    if (this.isInvalidStripeAccount) {
      this.subscriptions.add(
        this.organizationService
          .getStripeRedirectionUrl(
            this.organizationService.organization?.identifier,
            window.location.href
          )
          .subscribe({
            next: (data) => {
              if (data?.url) {
                this.stripeRedirectUrl = data?.url;
              }
            },
            error: () => {
              this.setDefaultStripeRedirectUrl();
            },
          })
      );
    } else {
      this.setDefaultStripeRedirectUrl();
    }
  }

  public get showRightContent(): boolean {
    return this.tipsConfig?.length > 0 && !this.hasSingleColumn;
  }

  public get showStepperTabBar(): boolean {
    return this.activeStep !== -1;
  }

  public get stripeLink(): string {
    return this.translateService.instant(
      'activity_creation.plans.stripe_toaster',
      {
        stripeLink: `<a href="${
          this.stripeRedirectUrl
        }" target="_blank" class="underline">${this.translateService.instant(
          'activity_creation.plans.stripe_redirect'
        )}</a>`,
      }
    );
  }

  public get isAllowExternalPayment() {
    return this.paramethorService.isTrue(
      ParamethorNameEnum.AllowExternalPayment
    );
  }

  public get isInvalidStripeAccount(): boolean {
    return (
      this.userService.getUser()?.providerName !== 'FED' &&
      this.organizationService.organization?.paymentProviderStatus !== 'LIVE' &&
      !this.paramethorService.isTrue(ParamethorNameEnum.AllowExternalPayment)
    );
  }

  public buildStepperTabBarCollection(): void {
    this.stepperTabBarCollection = this.stepperTabBarConfig
      .filter(
        (config) => !this.paramethorService.isTrue(config.toggleForDeactivation)
      )
      .map((config) => ({
        content: this.translateService.instant(config.contentKey),
      }));
  }

  public buildTips(): void {
    this.tips = (this.tipsConfig || []).map((config) => {
      if (config.descriptionKey !== '' && config.headerKey !== '') {
        const description = this.translateService.instant(
          config.descriptionKey
        );

        return {
          header: this.translateService.instant(config.headerKey),
          description: Array.isArray(config.descriptionKey)
            ? Object.values(description)
            : description,
          variant: config.variant || TipsVariantEnum.INFO,
          iconName: config.iconName || VtmnPlayIconTypeEnum.Information,
        };
      }
    });
  }

  private setDefaultStripeRedirectUrl(): void {
    this.stripeRedirectUrl = this.organizationService?.organization
      ?.paymentProviderIdentifier
      ? `https://dashboard.stripe.com/connect/accounts/${this.organizationService?.organization?.paymentProviderIdentifier}`
      : 'https://dashboard.stripe.com/settings/connect';
  }
}
